'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge, { AssetStatus } from '@/components/ui/StatusBadge';

import { toast } from 'sonner';
import AssetDetailPanel from './AssetDetailPanel';
import Modal from '@/components/ui/Modal';

type AssetType = 'server' | 'workstation' | 'firewall' | 'router' | 'switch' | 'gateway' | 'dns' | 'loadbalancer';

interface Asset {
  id: string;
  name: string;
  ip: string;
  mac: string;
  type: AssetType;
  os: string;
  location: string;
  riskScore: number;
  openAlerts: number;
  criticalAlerts: number;
  lastSeen: string;
  status: AssetStatus;
  owner: string;
  firmware: string;
  tags: string[];
  openPorts: number[];
  description: string;
}

const assets: Asset[] = [
  { id: 'AST-001', name: 'auth-server-01', ip: '10.0.1.15', mac: '00:1A:2B:3C:4D:01', type: 'server', os: 'Ubuntu 22.04 LTS', location: 'DC-A / Rack 12', riskScore: 91, openAlerts: 7, criticalAlerts: 3, lastSeen: '07:18:31', status: 'online', owner: 'Infra Team', firmware: 'N/A', tags: ['critical', 'auth', 'prod'], openPorts: [22, 443, 8080], description: 'Primary authentication server handling LDAP, OAuth2, and SAML services.' },
  { id: 'AST-002', name: 'webapp-prod-01', ip: '10.0.4.21', mac: '00:1A:2B:3C:4D:02', type: 'server', os: 'Debian 12 (Bookworm)', location: 'DC-A / Rack 8', riskScore: 55, openAlerts: 2, criticalAlerts: 0, lastSeen: '07:18:29', status: 'online', owner: 'Dev Team', firmware: 'N/A', tags: ['web', 'prod'], openPorts: [80, 443], description: 'Production web application server — primary customer-facing service.' },
  { id: 'AST-003', name: 'webapp-prod-02', ip: '10.0.4.22', mac: '00:1A:2B:3C:4D:03', type: 'server', os: 'Debian 12 (Bookworm)', location: 'DC-A / Rack 8', riskScore: 72, openAlerts: 3, criticalAlerts: 1, lastSeen: '07:18:28', status: 'online', owner: 'Dev Team', firmware: 'N/A', tags: ['web', 'prod', 'under-attack'], openPorts: [80, 443, 8443], description: 'Secondary production web server. Currently receiving SQL injection attempts.' },
  { id: 'AST-004', name: 'core-dns-01', ip: '10.0.1.5', mac: '00:1A:2B:3C:4D:04', type: 'dns', os: 'CentOS Stream 9', location: 'DC-A / Rack 2', riskScore: 88, openAlerts: 5, criticalAlerts: 2, lastSeen: '07:18:30', status: 'online', owner: 'Infra Team', firmware: 'BIND 9.18.4', tags: ['critical', 'dns', 'prod'], openPorts: [53, 953], description: 'Primary DNS resolver for internal and external zones.' },
  { id: 'AST-005', name: 'dmz-fw-01', ip: '10.0.2.1', mac: '00:1A:2B:3C:4D:05', type: 'firewall', os: 'pfSense 2.7.2', location: 'DC-A / DMZ Rack', riskScore: 44, openAlerts: 2, criticalAlerts: 0, lastSeen: '07:18:31', status: 'online', owner: 'Network Team', firmware: 'pfSense 2.7.2', tags: ['perimeter', 'firewall'], openPorts: [443, 8443], description: 'DMZ perimeter firewall. Handles ingress/egress filtering for public-facing services.' },
  { id: 'AST-006', name: 'rdp-gateway-01', ip: '10.0.6.9', mac: '00:1A:2B:3C:4D:06', type: 'gateway', os: 'Windows Server 2022', location: 'DC-B / Rack 5', riskScore: 67, openAlerts: 4, criticalAlerts: 1, lastSeen: '07:18:25', status: 'online', owner: 'IT Ops', firmware: 'N/A', tags: ['rdp', 'gateway', 'remote-access'], openPorts: [3389, 443], description: 'Remote Desktop Gateway for remote employee access.' },
  { id: 'AST-007', name: 'workstation-hr-14', ip: '10.0.8.113', mac: '00:1A:2B:3C:4D:07', type: 'workstation', os: 'Windows 11 Pro 23H2', location: 'Floor 3 / HR Dept', riskScore: 95, openAlerts: 1, criticalAlerts: 1, lastSeen: '07:04:12', status: 'quarantined', owner: 'HR Dept — User: K. Şahin', firmware: 'N/A', tags: ['quarantined', 'c2-suspected', 'hr'], openPorts: [], description: 'Quarantined — active C2 beacon detected. Isolated from network pending investigation.' },
  { id: 'AST-008', name: 'workstation-dev-07', ip: '10.0.5.201', mac: '00:1A:2B:3C:4D:08', type: 'workstation', os: 'macOS 15.3 (Sequoia)', location: 'Floor 2 / Dev Team', riskScore: 81, openAlerts: 2, criticalAlerts: 1, lastSeen: '07:11:33', status: 'degraded', owner: 'Dev Team — User: E. Çelik', firmware: 'N/A', tags: ['lateral-movement', 'dev'], openPorts: [22], description: 'Suspected lateral movement source. Network access partially restricted.' },
  { id: 'AST-009', name: 'db-server-02', ip: '10.0.9.3', mac: '00:1A:2B:3C:4D:09', type: 'server', os: 'Rocky Linux 9.3', location: 'DC-A / Rack 15', riskScore: 38, openAlerts: 1, criticalAlerts: 0, lastSeen: '07:18:27', status: 'online', owner: 'DBA Team', firmware: 'N/A', tags: ['database', 'prod', 'sensitive'], openPorts: [5432], description: 'Primary PostgreSQL database server. Contains customer PII — classified as sensitive.' },
  { id: 'AST-010', name: 'mail-server-01', ip: '10.0.3.17', mac: '00:1A:2B:3C:4D:10', type: 'server', os: 'Ubuntu 22.04 LTS', location: 'DC-B / Rack 3', riskScore: 29, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:18:26', status: 'online', owner: 'IT Ops', firmware: 'Postfix 3.7.4', tags: ['mail', 'prod'], openPorts: [25, 587, 993], description: 'Corporate mail server. Recently patched for privilege escalation CVE.' },
  { id: 'AST-011', name: 'vpn-gateway-01', ip: '10.0.2.8', mac: '00:1A:2B:3C:4D:11', type: 'gateway', os: 'OpenWrt 23.05', location: 'DC-A / DMZ Rack', riskScore: 52, openAlerts: 1, criticalAlerts: 0, lastSeen: '07:18:30', status: 'online', owner: 'Network Team', firmware: 'OpenWrt 23.05.2', tags: ['vpn', 'gateway', 'remote-access'], openPorts: [1194, 443], description: 'OpenVPN gateway for remote workforce access.' },
  { id: 'AST-012', name: 'lb-ext-01', ip: '10.0.2.5', mac: '00:1A:2B:3C:4D:12', type: 'loadbalancer', os: 'HAProxy 2.8', location: 'DC-A / DMZ Rack', riskScore: 33, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:18:31', status: 'online', owner: 'Infra Team', firmware: 'HAProxy 2.8.3', tags: ['lb', 'prod', 'dmz'], openPorts: [80, 443], description: 'External load balancer distributing traffic to webapp-prod-01 and webapp-prod-02.' },
  { id: 'AST-013', name: 'proxy-server-01', ip: '10.0.7.4', mac: '00:1A:2B:3C:4D:13', type: 'server', os: 'Ubuntu 20.04 LTS', location: 'DC-B / Rack 7', riskScore: 41, openAlerts: 1, criticalAlerts: 0, lastSeen: '07:17:55', status: 'online', owner: 'Network Team', firmware: 'Squid 5.9', tags: ['proxy', 'egress'], openPorts: [3128, 8080], description: 'Egress web proxy for internal users. SSL inspection enabled.' },
  { id: 'AST-014', name: 'core-switch-03', ip: '10.0.0.3', mac: '00:1A:2B:3C:4D:14', type: 'switch', os: 'Cisco IOS-XE 17.9', location: 'DC-A / Core Rack', riskScore: 18, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:18:31', status: 'online', owner: 'Network Team', firmware: 'Cisco IOS-XE 17.9.4', tags: ['network', 'core', 'critical-infra'], openPorts: [22, 443], description: 'Core distribution switch. Handles inter-VLAN routing for DC-A.' },
  { id: 'AST-015', name: 'siem-collector-01', ip: '10.0.1.2', mac: '00:1A:2B:3C:4D:15', type: 'server', os: 'Ubuntu 22.04 LTS', location: 'DC-A / Rack 1', riskScore: 12, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:18:31', status: 'online', owner: 'Security Team', firmware: 'N/A', tags: ['siem', 'collector', 'security-infra'], openPorts: [514, 601, 6514], description: 'NetSIEM log collection agent. Receives syslog, netflow, and auditd streams.' },
  { id: 'AST-016', name: 'workstation-fin-03', ip: '10.0.10.44', mac: '00:1A:2B:3C:4D:16', type: 'workstation', os: 'Windows 11 Pro 23H2', location: 'Floor 4 / Finance', riskScore: 22, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:15:12', status: 'online', owner: 'Finance — User: B. Demir', firmware: 'N/A', tags: ['finance', 'sensitive'], openPorts: [], description: 'Finance department workstation with access to ERP systems.' },
  { id: 'AST-017', name: 'backup-server-01', ip: '10.0.9.10', mac: '00:1A:2B:3C:4D:17', type: 'server', os: 'Rocky Linux 9.3', location: 'DC-B / Rack 12', riskScore: 15, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:00:00', status: 'offline', owner: 'Infra Team', firmware: 'N/A', tags: ['backup', 'scheduled-maintenance'], openPorts: [], description: 'Backup server. Currently offline for scheduled maintenance window (06:00–08:00 UTC).' },
  { id: 'AST-018', name: 'router-edge-01', ip: '10.0.0.1', mac: '00:1A:2B:3C:4D:18', type: 'router', os: 'Cisco IOS-XE 17.9', location: 'DC-A / Edge Rack', riskScore: 24, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:18:31', status: 'online', owner: 'Network Team', firmware: 'Cisco IOS-XE 17.9.4', tags: ['edge', 'router', 'critical-infra'], openPorts: [22, 443], description: 'Edge router connecting to upstream ISP. BGP peering with two providers.' },
  { id: 'AST-019', name: 'workstation-cto-01', ip: '10.0.11.5', mac: '00:1A:2B:3C:4D:19', type: 'workstation', os: 'macOS 15.3 (Sequoia)', location: 'Floor 5 / Executive', riskScore: 31, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:10:44', status: 'online', owner: 'Executive — User: A. Yıldız', firmware: 'N/A', tags: ['executive', 'high-value'], openPorts: [], description: 'CTO workstation. High-value target — enhanced monitoring enabled.' },
  { id: 'AST-020', name: 'ntp-server-01', ip: '10.0.1.8', mac: '00:1A:2B:3C:4D:20', type: 'server', os: 'Ubuntu 22.04 LTS', location: 'DC-A / Rack 2', riskScore: 8, openAlerts: 0, criticalAlerts: 0, lastSeen: '07:18:31', status: 'online', owner: 'Infra Team', firmware: 'chrony 4.3', tags: ['ntp', 'infra'], openPorts: [123], description: 'Internal NTP server synced to external stratum-1 sources.' },
];

const assetTypeConfig: Record<AssetType, { icon: string; label: string; color: string }> = {
  server: { icon: 'ServerIcon', label: 'Server', color: 'text-blue-400' },
  workstation: { icon: 'ComputerDesktopIcon', label: 'Workstation', color: 'text-zinc-400' },
  firewall: { icon: 'ShieldCheckIcon', label: 'Firewall', color: 'text-emerald-400' },
  router: { icon: 'ArrowPathIcon', label: 'Router', color: 'text-purple-400' },
  switch: { icon: 'ArrowsRightLeftIcon', label: 'Switch', color: 'text-indigo-400' },
  gateway: { icon: 'GlobeAltIcon', label: 'Gateway', color: 'text-amber-400' },
  dns: { icon: 'MagnifyingGlassCircleIcon', label: 'DNS', color: 'text-orange-400' },
  loadbalancer: { icon: 'ScaleIcon', label: 'Load Balancer', color: 'text-teal-400' },
};

export default function NetworkAssetsTable() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<AssetType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'all'>('all');
  const [riskFilter, setRiskFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [quarantineTarget, setQuarantineTarget] = useState<Asset | null>(null);

  const filtered = assets.filter((a) => {
    if (search) {
      const q = search.toLowerCase();
      if (!a.name.includes(q) && !a.ip.includes(q) && !a.mac.toLowerCase().includes(q) && !a.os.toLowerCase().includes(q) && !a.owner.toLowerCase().includes(q)) return false;
    }
    if (typeFilter !== 'all' && a.type !== typeFilter) return false;
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (riskFilter !== 'all') {
      if (riskFilter === 'critical' && a.riskScore < 80) return false;
      if (riskFilter === 'high' && (a.riskScore < 60 || a.riskScore >= 80)) return false;
      if (riskFilter === 'medium' && (a.riskScore < 30 || a.riskScore >= 60)) return false;
      if (riskFilter === 'low' && a.riskScore >= 30) return false;
    }
    return true;
  });

  const handleQuarantine = (asset: Asset) => {
    // Backend integration: POST /api/assets/:id/quarantine
    toast.success(`${asset.name} isolated from network — quarantine applied`);
    setQuarantineTarget(null);
  };

  const riskColor = (score: number) => {
    if (score >= 80) return 'text-red-400';
    if (score >= 60) return 'text-orange-400';
    if (score >= 30) return 'text-amber-400';
    return 'text-emerald-400';
  };

  const riskBarColor = (score: number) => {
    if (score >= 80) return 'bg-red-500';
    if (score >= 60) return 'bg-orange-500';
    if (score >= 30) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Assets', value: assets.length, color: 'text-zinc-200', bg: 'border-zinc-800' },
          { label: 'Online', value: assets.filter(a => a.status === 'online').length, color: 'text-emerald-400', bg: 'border-emerald-500/20 bg-emerald-500/5' },
          { label: 'Quarantined', value: assets.filter(a => a.status === 'quarantined').length, color: 'text-purple-400', bg: 'border-purple-500/20 bg-purple-500/5' },
          { label: 'High Risk (≥80)', value: assets.filter(a => a.riskScore >= 80).length, color: 'text-red-400', bg: 'border-red-500/20 bg-red-500/5' },
          { label: 'Offline', value: assets.filter(a => a.status === 'offline').length, color: 'text-zinc-500', bg: 'border-zinc-800' },
        ].map((s) => (
          <div key={s.label} className={`bg-zinc-900 border ${s.bg} rounded-xl px-4 py-3`}>
            <p className="text-[11px] font-medium uppercase tracking-widest text-zinc-500">{s.label}</p>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search name, IP, MAC, OS, owner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30"
          />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)} className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none cursor-pointer">
          <option value="all">All Types</option>
          {(Object.keys(assetTypeConfig) as AssetType[]).map((t) => (
            <option key={t} value={t}>{assetTypeConfig[t].label}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none cursor-pointer">
          <option value="all">All Statuses</option>
          {(['online', 'degraded', 'offline', 'quarantined'] as AssetStatus[]).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value as any)} className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none cursor-pointer">
          <option value="all">All Risk Levels</option>
          <option value="critical">Critical (≥80)</option>
          <option value="high">High (60–79)</option>
          <option value="medium">Medium (30–59)</option>
          <option value="low">Low (&lt;30)</option>
        </select>
        <span className="ml-auto text-xs text-zinc-600 font-mono">{filtered.length} assets</span>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800">
                {['Asset Name', 'IP Address', 'MAC Address', 'Type', 'OS / Firmware', 'Location', 'Risk Score', 'Open Alerts', 'Last Seen', 'Status', 'Owner', 'Actions'].map((h) => (
                  <th key={h} className="px-3 py-3 text-left font-semibold text-zinc-600 uppercase tracking-widest text-[10px] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((asset, idx) => {
                const typeCfg = assetTypeConfig[asset.type];
                return (
                  <tr
                    key={asset.id}
                    onClick={() => setSelectedAsset(asset)}
                    className={`
                      border-b border-zinc-800/40 hover:bg-zinc-800/40 transition-colors cursor-pointer group
                      ${idx % 2 !== 0 ? 'bg-zinc-900/30' : ''}
                      ${asset.status === 'quarantined' ? 'border-l-2 border-l-purple-500' : ''}
                      ${asset.riskScore >= 80 && asset.status !== 'quarantined' ? 'border-l-2 border-l-red-500/50' : ''}
                      ${selectedAsset?.id === asset.id ? 'bg-rose-500/5' : ''}
                    `}
                  >
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon name={typeCfg.icon as any} size={13} className={typeCfg.color} />
                        <span className="font-mono text-xs text-zinc-200 font-medium">{asset.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-amber-400/90 whitespace-nowrap">{asset.ip}</td>
                    <td className="px-3 py-3 font-mono text-zinc-600 whitespace-nowrap text-[11px]">{asset.mac}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`text-[11px] font-medium ${typeCfg.color}`}>{typeCfg.label}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-zinc-500 text-[11px]">{asset.os}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-zinc-600 text-[11px] font-mono">{asset.location}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${riskBarColor(asset.riskScore)}`}
                            style={{ width: `${asset.riskScore}%` }}
                          />
                        </div>
                        <span className={`font-mono text-xs font-bold tabular-nums ${riskColor(asset.riskScore)}`}>
                          {asset.riskScore}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {asset.openAlerts > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <span className={`font-mono text-xs font-bold tabular-nums ${asset.criticalAlerts > 0 ? 'text-red-400' : 'text-orange-400'}`}>
                            {asset.openAlerts}
                          </span>
                          {asset.criticalAlerts > 0 && (
                            <span className="text-[10px] text-red-400 font-semibold">({asset.criticalAlerts} crit)</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-zinc-600 font-mono">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-zinc-500 whitespace-nowrap text-[11px]">{asset.lastSeen}</td>
                    <td className="px-3 py-3 whitespace-nowrap"><StatusBadge status={asset.status} /></td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-zinc-600 text-[11px]">{asset.owner.split(' — ')[0]}</span>
                    </td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="relative group/tip">
                          <button
                            onClick={() => setSelectedAsset(asset)}
                            className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-300 transition-colors"
                            aria-label="View asset details"
                          >
                            <Icon name="EyeIcon" size={13} />
                          </button>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">View details</span>
                        </div>
                        {asset.status !== 'quarantined' && (
                          <div className="relative group/tip">
                            <button
                              onClick={() => setQuarantineTarget(asset)}
                              className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-purple-400 transition-colors"
                              aria-label="Quarantine asset — isolates from network"
                            >
                              <Icon name="NoSymbolIcon" size={13} />
                            </button>
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">Quarantine asset</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-zinc-800">
          <span className="text-xs text-zinc-600 font-mono">{filtered.length} asset{filtered.length !== 1 ? 's' : ''} shown — last inventory scan: 07:00:00 UTC</span>
        </div>
      </div>

      {/* Asset Detail Panel */}
      {selectedAsset && (
        <AssetDetailPanel asset={selectedAsset} onClose={() => setSelectedAsset(null)} onQuarantine={(a) => { setSelectedAsset(null); setQuarantineTarget(a); }} />
      )}

      {/* Quarantine Confirm Modal */}
      <Modal
        isOpen={!!quarantineTarget}
        onClose={() => setQuarantineTarget(null)}
        title="Confirm Network Quarantine"
        subtitle={quarantineTarget?.name}
        size="sm"
        footer={
          <>
            <button onClick={() => setQuarantineTarget(null)} className="px-4 py-2 text-sm text-zinc-400 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button
              onClick={() => quarantineTarget && handleQuarantine(quarantineTarget)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold rounded-lg transition-colors active:scale-95"
            >
              Quarantine Asset
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <Icon name="ExclamationTriangleIcon" size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300">
              Quarantining <span className="font-mono font-semibold text-purple-400">{quarantineTarget?.name}</span> will immediately isolate it from all network segments. Active sessions will be terminated.
            </p>
          </div>
          <p className="text-xs text-zinc-500">This action is logged and will trigger an automatic incident update. To restore network access, contact the SOC lead.</p>
        </div>
      </Modal>
    </div>
  );
}